import { useCallback, useState } from 'react';
import { usePlayerContext } from '../../ui-react/PlayerContext';
import { useStore } from '../../ui-react/hooks/useStore';
import { useEventBus } from '../../ui-react/hooks/useEventBus';
import { ViewerEyeIcon, ViewerHumanIcon } from '../../ui-react/icons';
import type { ApiData } from '../../core/types';
import styles from './StreamInfo.module.css';

interface StreamMeta {
  author: string;
  title: string;
  streamer: string;
  avatar: string;
  game: string;
  clipId: string;
  clipLink: string;
  viewCount?: number;
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

function readText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function createStreamMeta(data?: ApiData): StreamMeta {
  const clipId = readText(data?.channel_id) || (data?.id != null ? String(data.id) : '');
  const clipLink = readText(data?.link) || (clipId ? `/clip/${clipId}/channel/` : '');

  return {
    author: readText(data?.author),
    title: readText(data?.title) || readText(data?.channel_title),
    streamer: readText(data?.streamer_name) || readText(data?.streamer),
    avatar: readText(data?.streamer_avatar) || readText(data?.avatar),
    game: readText(data?.game?.title),
    clipId,
    clipLink,
    viewCount: readNumber(data?.views) ?? readNumber(data?.viewers),
  };
}

export function StreamInfo() {
  const { store, eventBus, config } = usePlayerContext();
  const viewers = useStore(store, useCallback(s => s.viewers, []));
  const [meta, setMeta] = useState<StreamMeta>(() => createStreamMeta(config.apiData));

  useEventBus(eventBus, 'api:data', useCallback((data: ApiData) => {
    const next = createStreamMeta(data);

    setMeta(prev => ({
      author: next.author || prev.author,
      title: next.title || prev.title,
      streamer: next.streamer || prev.streamer,
      avatar: next.avatar || prev.avatar,
      game: next.game || prev.game,
      clipId: next.clipId || prev.clipId,
      clipLink: next.clipLink || prev.clipLink,
      viewCount: next.viewCount ?? prev.viewCount,
    }));
  }, []));

  return (
    <div className={styles.topBlock}>
      <div className={styles.viewerBadge}>
        <ViewerHumanIcon />
        <span>{formatCount(viewers)}</span>
      </div>
      <div className={styles.clipInfo}>
        {meta.author && (
          <p className={styles.authorLine}>
            {'Автор '}
            <a
              href={meta.clipLink || '#'}
              target="_blank"
              rel="noreferrer"
              className={styles.authorName}
              data-no-toggle
            >
              {meta.author}
            </a>
          </p>
        )}
        {meta.title && <h4 className={styles.clipTitle}>{meta.title}</h4>}
        <div className={styles.streamerRow}>
          {meta.avatar && (
            <img src={meta.avatar} alt="" className={styles.streamerAvatar} />
          )}
          {meta.streamer && (
            <span className={styles.streamerName}>{meta.streamer}</span>
          )}
          {meta.game && (
            <>
              <span className={styles.playingIn}>играет в</span>
              <span className={styles.gamePill}>{meta.game}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
